import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { MessageService } from 'primeng/api';

import { StepsBannerErrorConfigModel } from '@Common/Misc/models/banner-error-config.model';
import { FocusHelper } from '@Common/Forms/helpers/focus.helper';
import { BriefingStepsEnum } from '../BriefingStepsNavigation/briefing-steps-navigation.component';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { BriefingModel } from '@Monitor/Briefings/models/briefing.model';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MailingListsService } from '@Common/MailingLists/services/mailing-lists.service';
import { combineLatest, filter, Subscription } from 'rxjs';
import { MailingListModel } from '@Common/MailingLists/models/mailingList.model';
import { BriefingBannerErrorIdEnum } from '@Monitor/Briefings/enums/briefing-banner-error-type.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { copy } from '@Common/Misc/helpers/copy/copy.helper';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { DigestService } from '@Monitor/Shared/services/digest.service';

@Component({
    selector: 'agpr-briefing-review-step',
    templateUrl: './briefing-review-step.component.html',
    styleUrls: ['./briefing-review-step.component.scss'],
    standalone: false
})
export class BriefingReviewStepComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly deliveryMethods = BriefingDeliveryMethodEnum;

    @Input() reservedUrl: string;
    @Input() draftPublished: boolean;
    @Input() compositionErrors: StepsBannerErrorConfigModel[];
    @Input() schedulingError: StepsBannerErrorConfigModel;
    @Input() set publishingErrors(errors: StepsBannerErrorConfigModel[]) {
        this.subjectError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.subject);
        this.fromNameError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.fromName);
        this.replyToError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.replyTo);
        this.recipientError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.recipient);
        this.mailingListError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.mailingList);
        this.rssFeedError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.rssFeed);
        this.deliveryOptionsError = errors.find(error => error.errorId === BriefingBannerErrorIdEnum.deliveryOptions);
    }

    @Input() numAutoGenMentions: number = 0;
    @Input() publishDraftInProgress: boolean = false;

    briefing: BriefingModel;
    allocatedMentionsCount: number;
    maxNumberOfMentionsInBriefing: number;
    mailingLists: MailingListModel[];
    totalMailingListRecipients: number = 0;

    subjectError: StepsBannerErrorConfigModel;
    fromNameError: StepsBannerErrorConfigModel;
    replyToError: StepsBannerErrorConfigModel;
    recipientError: StepsBannerErrorConfigModel;
    mailingListError: StepsBannerErrorConfigModel;
    rssFeedError: StepsBannerErrorConfigModel;
    deliveryOptionsError: StepsBannerErrorConfigModel;
    digestString: string;

    @Output() changeStep: EventEmitter<BriefingStepsEnum> = new EventEmitter();
    @Output() publishDraft: EventEmitter<null> = new EventEmitter();

    subscriptions: Subscription[] = [];

    constructor(
        public briefingsService: BriefingsService,
        public mailingListsService: MailingListsService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public digestService: DigestService
    ) {}

    ngOnInit(): void {
        this.maxNumberOfMentionsInBriefing = this.briefingsService.maxNumberOfMentionsInBriefing;
        this.subscriptions.push(
            combineLatest([
                this.briefingsService.getFocusedResource(),
                this.mailingListsService.getMailingLists(),
                this.digestService.all$
            ]).pipe(
                filter(([x]) => !!x)
            ).subscribe(([briefing, lists, digests]) => {
                this.briefing = briefing;
                if (this.briefing?.digests && digests?.length) {
                    this.digestString = this.briefing.digests.map(digestId => digests.find(digest => digest.id === digestId)?.name).join(', ');
                } else {
                    this.digestString = '';
                }

                if (!lists) {
                    return;
                }
                this.mailingLists = lists.filter(list => this.briefing.mailingListIds?.includes(list.id));
                this.totalMailingListRecipients = this.mailingListsService.getRecipientCount(this.briefing.mailingListIds);
            }),
            this.briefingsService.getCurrentBriefingAllocatedMentionsCountProvider().subscribe(
                allocatedMentionsCount => {
                    this.allocatedMentionsCount = allocatedMentionsCount;
                }
            )
        );
    }

    navigateToComposeStep() {
        this.changeStep.emit(BriefingStepsEnum.compose);
    }

    navigateToPublishingStep() {
        this.changeStep.emit(BriefingStepsEnum.publishingSettings);
    }

    focusErrorElement(error: StepsBannerErrorConfigModel) {
        if (error.tab === BriefingStepsEnum.compose) {
            this.navigateToComposeStep();
        } else {
            this.navigateToPublishingStep();
        }
        if (error.errorId) {
            requestAnimationFrame(() => {
                FocusHelper.focusError(error.errorId);
            });
        }
    }

    copyUrl(event: Event, url: string): void {
        if (!url) {
            return;
        }
        copy.text(url);
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.summary'),
            detail: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.detail'),
        });
        (event.target as HTMLElement).focus();
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
